"use client";

import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";
import { Container } from "@/components/layout/container";
import { useCartStore } from "@/lib/cart-store";
import { useEffect, useMemo, useOptimistic, useState, useTransition } from "react";
import {
  clearServerCart,
  removeFromServerCart,
  updateServerCartItem,
} from "./actions";

type ServerCart = {
  id: string;
  items: Array<{
    id: string;
    quantity: number;
    product: {
      id: string;
      slug: string;
      name: string;
      price: unknown;
      isGlitter: boolean;
      stock: number;
      category: {
        name: string;
      };
      images: Array<{
        imageUrl: string;
        alt: string | null;
      }>;
    };
  }>;
} | null;

type CartItemView = {
  id: string;
  productId: string;
  slug: string;
  name: string;
  price: number;
  image?: string | null;
  quantity: number;
  isGlitter?: boolean;
  categoryName?: string;
  stock: number;
};

function formatPrice(value: number) {
  return new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "EUR",
  }).format(value);
}

function normalizeServerCart(cart: ServerCart): CartItemView[] {
  if (!cart) return [];

  return cart.items.map((item) => ({
    id: item.id,
    productId: item.product.id,
    slug: item.product.slug,
    name: item.product.name,
    price: Number(item.product.price),
    image: item.product.images[0]?.imageUrl ?? null,
    quantity: item.quantity,
    isGlitter: item.product.isGlitter,
    categoryName: item.product.category.name,
    stock: item.product.stock,
  }));
}

export function CartPageClient({
  isAuthenticated,
  serverCart,
}: {
  isAuthenticated: boolean;
  serverCart: ServerCart;
}) {
  const localItems = useCartStore((state) => state.items);
  const localRemoveItem = useCartStore((state) => state.removeItem);
  const localAddItem = useCartStore((state) => state.addItem);
  const localDecreaseItem = useCartStore((state) => state.decreaseItem);
  const localClearCart = useCartStore((state) => state.clearCart);

  const searchParams = useSearchParams();
  const router = useRouter();
  const isCancelled = searchParams.get("cancelled") === "1";

  const serverItems = useMemo(() => normalizeServerCart(serverCart), [serverCart]);

  const baseItems = isAuthenticated
    ? serverItems
    : localItems.map((item) => ({
        id: item.id,
        productId: item.id,
        slug: item.slug,
        name: item.name,
        price: item.price,
        image: item.image ?? null,
        quantity: item.quantity,
        isGlitter: item.isGlitter,
        categoryName: item.categoryName,
        stock: 99,
      }));

  const [optimisticItems, setOptimisticItems] = useOptimistic(
    baseItems,
    (state: CartItemView[], next: CartItemView[]) => next
  );

  const [isPending, startTransition] = useTransition();
  const [isCheckoutLoading, setIsCheckoutLoading] = useState(false);

  const items = isAuthenticated ? optimisticItems : baseItems;

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    setOptimisticItems(serverItems);

    if (serverItems.length === 0) {
      localClearCart();
    }
  }, [isAuthenticated, serverItems, localClearCart, setOptimisticItems]);

  const totalPrice = items.reduce(
    (total, item) => total + item.price * item.quantity,
    0
  );

  const updateOptimisticQuantity = (productId: string, quantity: number) => {
    const next =
      quantity <= 0
        ? optimisticItems.filter((item) => item.productId !== productId)
        : optimisticItems.map((item) =>
            item.productId === productId ? { ...item, quantity } : item
          );

    setOptimisticItems(next);
  };

  const removeOptimisticItem = (productId: string) => {
    setOptimisticItems(
      optimisticItems.filter((item) => item.productId !== productId)
    );
  };
  

  const handleIncrease = (item: CartItemView) => {
    if (isAuthenticated) {
      const nextQuantity = item.quantity + 1;
      if (nextQuantity > item.stock) return;

      updateOptimisticQuantity(item.productId, nextQuantity);

      localAddItem({
        id: item.productId,
        slug: item.slug,
        name: item.name,
        price: item.price,
        image: item.image ?? null,
        isGlitter: item.isGlitter,
        categoryName: item.categoryName,
      });

      startTransition(async () => {
        try {
          await updateServerCartItem(item.productId, nextQuantity);
          router.refresh();
        } catch (error) {
          console.error(error);
        }
      });

      return;
    }

    localAddItem({
      id: item.productId,
      slug: item.slug,
      name: item.name,
      price: item.price,
      image: item.image ?? null,
      isGlitter: item.isGlitter,
      categoryName: item.categoryName,
    });
  };

  const handleDecrease = (item: CartItemView) => {
    if (isAuthenticated) {
      const nextQuantity = item.quantity - 1;
      updateOptimisticQuantity(item.productId, nextQuantity);

      localDecreaseItem(item.productId);

      startTransition(async () => {
        try {
          await updateServerCartItem(item.productId, nextQuantity);
          router.refresh();
        } catch (error) {
          console.error(error);
        }
      });

      return;
    }

    localDecreaseItem(item.productId);
  };

  const handleRemove = (item: CartItemView) => {
    if (isAuthenticated) {
      removeOptimisticItem(item.productId);

      localRemoveItem(item.productId);

      startTransition(async () => {
        try {
          await removeFromServerCart(item.productId);
          router.refresh();
        } catch (error) {
          console.error(error);
        }
      });

      return;
    }

    localRemoveItem(item.productId);
  };

  const handleClear = () => {
    if (isAuthenticated) {
      setOptimisticItems([]);

      localClearCart();

      startTransition(async () => {
        try {
          await clearServerCart();
          router.refresh();
        } catch (error) {
          console.error(error);
        }
      });

      return;
    }

    localClearCart();
  };

  const handleCheckout = async () => {
    if (!isAuthenticated) {
      window.location.href = "/connexion?callbackUrl=/panier";
      return;
    }

    try {
      setIsCheckoutLoading(true);

      const res = await fetch("/api/checkout", {
        method: "POST",
      });

      const data = await res.json();

      if (!res.ok) {
        if (data.error === "PROFILE_INCOMPLETE") {
          window.location.href = "/compte?completeProfile=1";
          return;
        }

        throw new Error(data.error || "Erreur de paiement");
      }

      if (data.url) {
        window.location.href = data.url;
        return;
      }

      throw new Error("URL de paiement introuvable");
    } catch (error) {
        console.error(error);
        alert(
          error instanceof Error
            ? error.message
            : "Impossible de lancer le paiement pour le moment."
        );
      } finally {
      setIsCheckoutLoading(false);
    }
  };

  return (
    <section className="section">
      <Container>
        {isCancelled ? (
          <div className="mb-6 rounded-[22px] border border-[var(--border)] bg-[var(--card)] p-5 text-sm leading-7 text-[var(--foreground)]/75 shadow-[var(--shadow)]">
            Le paiement a été interrompu. Votre sélection est toujours disponible dans le panier.
          </div>
        ) : null}
        <div className="mb-10 max-w-2xl">
          <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-[var(--accent)]/85">
            Panier
          </p>
          <h1 className="mt-4 text-5xl leading-[0.96] sm:text-6xl">
            Mon panier
          </h1>
          <p className="mt-4 text-base leading-7 text-[var(--foreground)]/74">
            Retrouvez ici les laines et kits sélectionnés avant le passage au paiement.
          </p>
        </div>

        {items.length === 0 ? (
          <div className="overflow-hidden rounded-[34px] border border-[var(--border)] bg-[var(--card)] shadow-[var(--shadow)]">
            <div className="relative px-8 py-14 sm:px-12 sm:py-18">
              <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(255,63,142,0.08),transparent_30%),radial-gradient(circle_at_bottom_left,rgba(201,90,58,0.08),transparent_28%)]" />

              <div className="relative mx-auto max-w-3xl text-center">
                <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full border border-[var(--border)] bg-white/80 text-[var(--accent)] shadow-[0_14px_30px_rgba(111,63,74,0.08)]">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.9"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="h-9 w-9"
                  >
                    <circle cx="8" cy="21" r="1" />
                    <circle cx="19" cy="21" r="1" />
                    <path d="M2.5 3H5l2.3 10.2a1 1 0 0 0 1 .8h9.7a1 1 0 0 0 1-.8L21 6H6.2" />
                  </svg>
                </div>

                <p className="mt-8 font-mono text-[11px] uppercase tracking-[0.18em] text-[var(--accent)]/80">
                  Panier vide
                </p>

                <h2 className="mt-4 text-4xl leading-[0.95] sm:text-5xl">
                  Votre sélection reste à inventer.
                </h2>

                <p className="mx-auto mt-6 max-w-2xl text-sm leading-7 text-[var(--foreground)]/70 sm:text-base">
                  Parcourez la boutique, laissez-vous guider par les couleurs, les matières et les créations du moment,
                  puis composez un panier à votre rythme.
                </p>

                <div className="mt-10 flex flex-col items-center justify-center gap-3 sm:flex-row">
                  <Link
                    href="/boutique"
                    className="inline-flex items-center justify-center rounded-full bg-[var(--primary)] px-6 py-3 text-sm font-medium text-white transition hover:opacity-90"
                  >
                    Explorer la boutique
                  </Link>

                  <Link
                    href="/compte"
                    className="inline-flex items-center justify-center rounded-full border border-[var(--border)] px-6 py-3 text-sm font-medium transition hover:border-[var(--accent)] hover:text-[var(--accent)]"
                  >
                    Voir mon compte
                  </Link>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="grid gap-8 lg:grid-cols-[1.1fr_0.9fr]">
            <div className="grid gap-5">
              {items.map((item) => (
                <article
                  key={item.id}
                  className="rounded-[28px] border border-[var(--border)] bg-[var(--card)] p-5 shadow-[var(--shadow)]"
                >
                  <div className="flex flex-col gap-5 sm:flex-row sm:items-center">
                    <div className="h-28 w-full overflow-hidden rounded-[20px] border border-[var(--border)] bg-white/50 sm:w-28">
                      {item.image ? (
                        <img
                          src={item.image}
                          alt={item.name}
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <div className="h-full w-full bg-[linear-gradient(145deg,rgba(255,63,142,0.16),rgba(201,90,58,0.08),rgba(246,241,237,0.9))]" />
                      )}
                    </div>

                    <div className="flex-1">
                      <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-[var(--primary)]">
                        {item.categoryName || "Produit"}
                      </p>
                      <h2 className="mt-2 text-2xl leading-tight">{item.name}</h2>

                      <div className="mt-3 flex flex-wrap gap-3">
                        {item.isGlitter ? (
                          <span className="rounded-full bg-[var(--primary)] px-3 py-1 text-[11px] font-medium text-white">
                            À paillettes
                          </span>
                        ) : (
                          <span className="rounded-full border border-[var(--border)] px-3 py-1 text-[11px] text-[var(--foreground)]/60">
                            Sans paillettes
                          </span>
                        )}
                      </div>

                      <p className="mt-4 text-lg font-semibold">
                        {formatPrice(item.price)}
                      </p>
                    </div>

                    <div className="flex flex-col gap-3 sm:items-end">
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => handleDecrease(item)}
                          disabled={isPending}
                          className="h-10 w-10 rounded-full border border-[var(--border)] bg-white/60 text-lg transition hover:border-[var(--primary)] hover:text-[var(--primary)] disabled:opacity-50"
                        >
                          -
                        </button>

                        <span className="min-w-6 text-center text-sm font-medium">
                          {item.quantity}
                        </span>

                        <button
                          onClick={() => handleIncrease(item)}
                          disabled={isPending || item.quantity >= item.stock}
                          className="h-10 w-10 rounded-full border border-[var(--border)] bg-white/60 text-lg transition hover:border-[var(--primary)] hover:text-[var(--primary)] disabled:opacity-50"
                        >
                          +
                        </button>
                      </div>

                      <button
                        onClick={() => handleRemove(item)}
                        disabled={isPending}
                        className="text-sm text-[var(--foreground)]/60 transition hover:text-[var(--primary)] disabled:opacity-50"
                      >
                        Supprimer
                      </button>
                    </div>
                  </div>
                </article>
              ))}
            </div>

            <aside className="h-fit rounded-[30px] border border-[var(--border)] bg-white/55 p-6 shadow-[var(--shadow)]">
              <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-[var(--accent)]/85">
                Résumé
              </p>
              <h2 className="mt-3 text-3xl">Commande</h2>

              <div className="mt-6 space-y-4 border-t border-[var(--border)] pt-6">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-[var(--foreground)]/72">Sous-total</span>
                  <span className="font-medium">{formatPrice(totalPrice)}</span>
                </div>

                <div className="flex items-center justify-between text-sm">
                  <span className="text-[var(--foreground)]/72">Livraison</span>
                  <span className="font-medium">Calculée à l’étape suivante</span>
                </div>
              </div>

              <div className="mt-6 flex items-center justify-between border-t border-[var(--border)] pt-6">
                <span className="text-base font-medium">Total estimé</span>
                <span className="text-2xl font-semibold">
                  {formatPrice(totalPrice)}
                </span>
              </div>

              <div className="mt-8 flex flex-col gap-3">
                <button
                  onClick={handleCheckout}
                  disabled={isCheckoutLoading || isPending}
                  className="rounded-full bg-[var(--primary)] px-6 py-3 text-sm font-medium text-white transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {isCheckoutLoading ? "Redirection..." : "Passer au paiement"}
                </button>

                <button
                  onClick={handleClear}
                  disabled={isPending}
                  className="rounded-full border border-[var(--border)] px-6 py-3 text-sm font-medium transition hover:border-[var(--accent)] hover:text-[var(--accent)] disabled:opacity-50"
                >
                  Vider le panier
                </button>
              </div>
            </aside>
          </div>
        )}
      </Container>
    </section>
  );
}